// [PURPOSE]: Party Context - Centralized party and multiplayer state management
// [WHY]: Extract party-related state from monolithic App.jsx (16,437 lines)
// [INTEGRATION]: Provides party sync, shared vault, and DM tools via React Context
// [CREATED]: October 24, 2025 - Phase 4A: Architectural Refactoring
// [FIX - NOV 3, 2025]: Changed from context hook to props to fix initialization timing issue

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ref, remove, set, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { useEnhancedPartySync } from '../hooks/useEnhancedPartySync';
import { database } from '../firebase/config';
import { systemBus, EVENTS } from '../services/systemBus';
import { safeLocalStorageSet } from '../utils/storageValidator';
import { hashPartyPassword, verifyPartyPassword } from '../utils/partyPasswordHash';
import { buildCreatePartyPayload, buildJoinPartyMemberPayload } from '../services/partyWriteHelpers';

/**
 * PartyContext provides all party-related state and functions
 *
 * This context manages:
 * - Party ID and name (with localStorage persistence)
 * - Real-time party synchronization via Firebase
 * - Party members and activity log
 * - Shared vault for party item storage
 * - DM features (push content, control party)
 * - Spell effects on allies
 * - Party connection status
 *
 * @param {object} playerStats - Character stats from CharacterContext
 * @param {array} activityLog - Activity log from CharacterContext
 */
const PartyContext = createContext(null);

export const PartyProvider = ({ children, playerStats = {}, activityLog = [] }) => {
  // [FIX - NOV 3, 2025]: Receive playerStats and activityLog as props instead of calling useCharacter()
  // [WHY]: Prevents context initialization timing issues where PartyProvider renders before
  //        CharacterProvider has fully initialized its context value
  // ============================================================================
  // CORE PARTY STATE
  // ============================================================================

  const [partyId, setPartyId] = useState(() => localStorage.getItem('partyId') || null);
  const [partyName, setPartyName] = useState(() => localStorage.getItem('partyName') || '');

  // ============================================================================
  // PARTY SYNC HOOK (Firebase Real-Time Synchronization)
  // ============================================================================

  // [INTEGRATION]: Use the enhanced party sync hook for real-time Firebase sync
  const {
    partyMembers,
    partyActivityLog,
    isConnected,
    syncError,
    // DM Features
    isDM,
    canPushContent,
    // Shared Vault
    sharedVaultItems,
    addToSharedVault,
    takeFromSharedVault,
    removeFromSharedVault,
    // DM Push Content
    pushShopToParty,
    pushNoteToParty,
    pushCalendarEventToParty,
    pushLootRollToParty,
    pushedContent,
    // Spell Effects on Allies
    applyEffectToAlly,
    removeEffectFromAlly,
    activeEffects,
    allPartyEffects,
    // [BATCH 13b / P1 A-9 — APR 2026]: Caster-side effect cleanup so
    //   spells we authored on allies don't linger after we leave.
    cleanupCasterEffects,
    cleanupMemberFootprints,
  } = useEnhancedPartySync(partyId, playerStats?.id, playerStats, activityLog);

  // ============================================================================
  // PARTY UI STATE
  // ============================================================================

  const [showSharedVault, setShowSharedVault] = useState(false);
  const [selectedAllyTarget, setSelectedAllyTarget] = useState(null); // For casting spells on allies

  // ============================================================================
  // PERSISTENCE (Save party info to localStorage)
  // ============================================================================

  // Persist partyId to localStorage
  useEffect(() => {
    if (partyId) {
      safeLocalStorageSet('partyId', partyId);
    } else {
      localStorage.removeItem('partyId');
    }
  }, [partyId]);

  // Persist partyName to localStorage
  useEffect(() => {
    if (partyName) {
      safeLocalStorageSet('partyName', partyName);
    } else {
      localStorage.removeItem('partyName');
    }
  }, [partyName]);

  // ============================================================================
  // PARTY ACTIONS
  // ============================================================================

  /**
   * Join a party
   * @param {string} newPartyId - Party ID to join
   * @param {string} newPartyName - Party name
   */
  const joinParty = useCallback((newPartyId, newPartyName = '') => {
    setPartyId(newPartyId);
    setPartyName(newPartyName);

    // [INTEGRATION]: Emit PARTY_JOINED event
    systemBus.emit(EVENTS.PARTY_JOINED, {
      partyId: newPartyId,
      partyName: newPartyName,
      characterName: playerStats?.name
    });

  }, [playerStats?.name]);

  // ============================================================================
  // CREATE / JOIN HANDLERS (Firebase writes + session-only password cache)
  // [SPEC 2026-05-11]: Handlers extracted from SettingsPanel.PartyCreateJoinForm
  //   so they can be reused from the new PartyCreateJoinSplash (Task 5+).
  // ============================================================================

  // [SPEC 2026-05-11]: Session-only cache so DM can re-share the cleartext
  //   password from PartyShareInviteModal until the page refreshes. Never
  //   written to localStorage (security: would persist plaintext).
  const cachedCreatePasswordRef = useRef(null);

  const createPartyHandler = useCallback(async ({
    partyName: inputPartyName,
    customPartyId,
    password,
    characterId,
    characterName,
    hitPoints,
    maxHitPoints,
    authUid,
  }) => {
    if (!characterId) {
      return { ok: false, error: 'Save your character first — your party needs a DM identity attached.' };
    }
    // [I-1 FIX]: Clear stale cache before attempting create. Prevents
    //   "DM created party A with password, left, then created party B with
    //   no password — share-modal still shows party A's password" scenario.
    cachedCreatePasswordRef.current = null;
    try {
      const CUSTOM_ID_PATTERN = /^[a-zA-Z0-9_-]{6,32}$/;
      let id = (customPartyId || '').trim();
      if (id) {
        if (!CUSTOM_ID_PATTERN.test(id)) {
          return { ok: false, error: 'ID must be 6-32 chars: letters, numbers, underscore, hyphen' };
        }
        if (database) {
          // [C-1 FIX]: collision-check get() now inside try/catch so a Firebase
          //   network/permission error becomes { ok: false, error } instead of
          //   an unhandled rejection.
          const existing = await get(ref(database, `parties/${id}/metadata`));
          if (existing.exists()) {
            return { ok: false, error: `That ID is taken — try another` };
          }
        }
      } else {
        id = `party_${Date.now()}`;
      }
      const trimmedPassword = (password || '').trim();
      const passwordHash = trimmedPassword
        ? await hashPartyPassword(trimmedPassword, id)
        : '';
      const partyDisplayName = `${characterName}'s Party`;
      const trimmedInputName = (inputPartyName || '').trim();
      if (database) {
        await set(
          ref(database, `parties/${id}`),
          buildCreatePartyPayload({
            authUid,
            characterId,
            characterName,
            hitPoints,
            maxHitPoints,
            partyDisplayName,
            partyName: trimmedInputName,
            passwordHash,
          })
        );
      }
      cachedCreatePasswordRef.current = trimmedPassword || null;
      // [I-3 NOTE]: When user input is empty, return.partyName falls back to
      //   the auto-synthesized partyDisplayName. Firebase metadata.partyName
      //   is OMITTED in that case (buildCreatePartyPayload skips the field).
      //   Readers should prefer metadata.partyName ?? metadata.name (root).
      return { ok: true, partyId: id, partyName: trimmedInputName || partyDisplayName };
    } catch (err) {
      console.error('[PartyContext] createPartyHandler failed:', err);
      return { ok: false, error: `Couldn't create — ${err.message || 'network error'}` };
    }
  }, []);

  const joinPartyHandler = useCallback(async ({
    inviteCode,
    password,
    characterId,
    characterName,
    hitPoints,
    maxHitPoints,
    authUid,
  }) => {
    const id = (inviteCode || '').trim();
    if (!id) return { ok: false, error: 'Enter an invite code' };
    if (!database) return { ok: false, error: "Couldn't join — offline" };
    // [m-1 FIX]: Explicit guards with friendly messages so internal sentinel
    //   strings from buildJoinPartyMemberPayload (which throws via ensureAuthUid
    //   / ensureCharacterId) never leak to the user via the catch path.
    if (!authUid) return { ok: false, error: 'Sign in required to join a party' };
    if (!characterId) return { ok: false, error: 'Save your character first — joining a party needs a character ID' };
    try {
      const snap = await get(ref(database, `parties/${id}/metadata`));
      if (!snap.exists()) return { ok: false, error: `No party with that invite code` };
      const meta = snap.val();
      if (meta.passwordHash) {
        const ok = await verifyPartyPassword(password || '', id, meta.passwordHash, meta.password);
        if (!ok) return { ok: false, error: 'Incorrect party password' };
      }
      await set(
        ref(database, `parties/${id}/members/${characterId}`),
        buildJoinPartyMemberPayload({ authUid, characterId, characterName, hitPoints, maxHitPoints })
      );
      await set(ref(database, `parties/${id}/memberUids/${authUid}`), true);
      const resolvedName = meta.partyName || meta.name || 'Party';
      return { ok: true, partyId: id, partyName: resolvedName };
    } catch (err) {
      console.error('[PartyContext] joinPartyHandler failed:', err);
      return { ok: false, error: `Couldn't join — ${err.message || 'network error'}` };
    }
  }, []);

  // [I-2 FIX]: Renamed from consumeCachedCreatePassword. The function is a
  //   non-destructive peek, not a consume-and-clear. PartyShareInviteModal can
  //   re-open multiple times within the same DM session for the same party and
  //   read the same cleartext each time. The ref clears only on leaveParty or
  //   on a new createPartyHandler attempt (see I-1 fix).
  const getCachedCreatePassword = useCallback(() => {
    const v = cachedCreatePasswordRef.current;
    return typeof v === 'string' && v.length > 0 ? v : null;
  }, []);

  /**
   * Leave the current party
   * [BATCH 13b / P1 A-9 — APR 2026]: Cleanup caster-authored effects
   *   on Firebase BEFORE clearing partyId so the cleanup helper still
   *   has the partyId reference. Best-effort — if the Firebase remove
   *   fails (network error, permission), we still clear local state so
   *   the user isn't stuck "in" a party they no longer want to be in.
   */
  const leaveParty = useCallback(async () => {
    const oldPartyId = partyId;
    const oldPartyName = partyName;
    const characterId = playerStats?.id;
    const authUid = getAuth()?.currentUser?.uid;

    // [I-1 FIX]: Clear cached create-password so a subsequent createPartyHandler
    //   for a different party doesn't see this party's stale cleartext.
    cachedCreatePasswordRef.current = null;

    if (cleanupCasterEffects) {
      try {
        await cleanupCasterEffects();
      } catch (e) {
        // Log but proceed — local state must still clear.
        console.warn('[PartyContext] cleanupCasterEffects failed:', e);
      }
    }

    // [PSR-009 FIX — MAY 2026]: Cleanup vault ownership transfer + viewedBy/
    //   claims so the leaving member's stale fingerprints don't linger.
    //   Best-effort; failures logged.
    if (cleanupMemberFootprints) {
      try {
        await cleanupMemberFootprints();
      } catch (e) {
        console.warn('[PartyContext] cleanupMemberFootprints failed:', e);
      }
    }

    // [PSR-003 FIX 2026-05-09]: Actually remove the user from the Firebase party
    // BEFORE clearing local state. Pre-fix, leaveParty only nulled local state —
    // the user retained full read/write access to "their" party because their
    // memberUids/$uid entry stayed in place (root rule passes for any UID still
    // in memberUids). Now we remove memberUids first (security), then members
    // (data cleanup). Failures are logged but don't block local state clearing,
    // because a stuck "in a party" state is also bad.
    if (oldPartyId && database) {
      if (authUid) {
        try {
          await remove(ref(database, `parties/${oldPartyId}/memberUids/${authUid}`));
        } catch (e) {
          console.warn('[PartyContext] memberUids cleanup failed:', e);
        }
      }
      if (characterId) {
        try {
          await remove(ref(database, `parties/${oldPartyId}/members/${characterId}`));
        } catch (e) {
          console.warn('[PartyContext] members cleanup failed:', e);
        }
      }
    }

    setPartyId(null);
    setPartyName('');

    // [INTEGRATION]: Emit PARTY_LEFT event
    systemBus.emit(EVENTS.PARTY_LEFT, {
      partyId: oldPartyId,
      partyName: oldPartyName,
      characterName: playerStats?.name
    });

  }, [partyId, partyName, playerStats?.id, playerStats?.name, cleanupCasterEffects, cleanupMemberFootprints]);

  /**
   * Update party name
   * @param {string} newName - New party name
   */
  const updatePartyName = useCallback((newName) => {
    setPartyName(newName);
  }, []);

  /**
   * Get party member by ID
   * @param {string} memberId - Member ID to find
   * @returns {Object|null} Party member or null if not found
   */
  const getPartyMember = useCallback((memberId) => {
    // [PSR-021 FIX — MAY 2026]: Stricter guard at the top — empty string,
    //   null, undefined all short-circuit to null without doing the lookup.
    //   Pre-fix this still returned null but only by accident (lookup of
    //   `undefined`/`''` keys → undefined → ` || null` → null). Explicit
    //   guard makes the contract obvious and avoids the lookup itself.
    if (!memberId) return null;
    if (!partyMembers) return null;
    // [BATCH 13a / P1 D-27 — APR 2026]: partyMembers is set via
    //   setPartyMembers(snapshot.val() || {}) in useEnhancedPartySync —
    //   a Firebase-shaped object keyed by characterId, not an array.
    //   Pre-fix `.find` threw `TypeError: partyMembers.find is not a
    //   function` on every call. Object access by key is the canonical
    //   shape; array fallback covers legacy in-memory snapshots.
    if (Array.isArray(partyMembers)) {
      return partyMembers.find(m => m.id === memberId) || null;
    }
    return partyMembers[memberId] || null;
  }, [partyMembers]);

  /**
   * Get party member by name
   * @param {string} memberName - Member name to find
   * @returns {Object|null} Party member or null if not found
   */
  const getPartyMemberByName = useCallback((memberName) => {
    if (!partyMembers) return null;
    // [BATCH 13a / P1 D-27 — APR 2026]: See getPartyMember above.
    //   Object.values traversal handles the Firebase shape; array
    //   fallback covers legacy in-memory snapshots.
    const target = (memberName || '').toLowerCase();
    if (Array.isArray(partyMembers)) {
      return partyMembers.find(m => m.name?.toLowerCase() === target) || null;
    }
    return Object.values(partyMembers).find(
      m => m && m.name && m.name.toLowerCase() === target
    ) || null;
  }, [partyMembers]);

  // ============================================================================
  // SHARED VAULT ACTIONS (Wrapped with Events)
  // ============================================================================

  /**
   * Add item to shared vault
   * [FA-004 FIX 2026-05-09]: Now async + awaits the underlying Firebase
   *   write. Pre-fix the wrapper was fire-and-forget — the
   *   SHARED_VAULT_ITEM_ADDED event fired even when the Firebase write
   *   failed (rules denial, network error). Subscribers acted on a
   *   "success" that never happened.
   * @param {Object} item - Item to add
   * @returns {Promise<boolean>} true if Firebase write succeeded
   */
  const addItemToSharedVault = useCallback(async (item) => {
    if (!addToSharedVault) return false;

    const result = await addToSharedVault(item);
    if (result) {
      // [INTEGRATION]: Emit SHARED_VAULT_ITEM_ADDED event ONLY on success
      systemBus.emit(EVENTS.SHARED_VAULT_ITEM_ADDED, {
        item,
        characterName: playerStats?.name
      });
    }
    return result;
  }, [addToSharedVault, playerStats?.name]);

  /**
   * Take item from shared vault
   * [FA-004 FIX 2026-05-09]: Now async + awaits the underlying Firebase
   *   delete. takeFromSharedVault returns the claimed item on success,
   *   null on failure — gate the event on that result.
   * @param {string} itemId - Item ID to take
   * @returns {Promise<Object|null>} the claimed item on success
   */
  const takeItemFromSharedVault = useCallback(async (itemId) => {
    if (!takeFromSharedVault) return null;

    // [PSR-001 FIX 2026-05-09]: sharedVaultItems is a Firebase OBJECT, not an
    // array. snapshot.val() returns {} keyed by push-id. Pre-fix, .find threw
    // a TypeError and the SHARED_VAULT_ITEM_TAKEN systemBus event never fired.
    // Same bug class as the partyMembers.find regression fixed in Batch 13a.
    const item = sharedVaultItems?.[itemId]?.item || null;
    const claimed = await takeFromSharedVault(itemId);

    // [INTEGRATION]: Emit SHARED_VAULT_ITEM_TAKEN event ONLY on success
    if (claimed && item) {
      systemBus.emit(EVENTS.SHARED_VAULT_ITEM_TAKEN, {
        item,
        characterName: playerStats?.name
      });
    }
    return claimed;
  }, [takeFromSharedVault, sharedVaultItems, playerStats?.name]);

  /**
   * Remove item from shared vault (DM only)
   * [FA-004 FIX 2026-05-09]: Now async + awaits the underlying Firebase
   *   delete. removeFromSharedVault returns true/false — gate the event
   *   on a truthy result.
   * @param {string} itemId - Item ID to remove
   * @returns {Promise<boolean>} true if Firebase delete succeeded
   */
  const removeItemFromSharedVault = useCallback(async (itemId) => {
    if (!removeFromSharedVault || !isDM) return false;

    // [PSR-001 FIX 2026-05-09]: see takeItemFromSharedVault — sharedVaultItems
    // is an object, not an array.
    const item = sharedVaultItems?.[itemId]?.item || null;
    const result = await removeFromSharedVault(itemId);

    // [INTEGRATION]: Emit SHARED_VAULT_ITEM_REMOVED event ONLY on success
    if (result && item) {
      systemBus.emit(EVENTS.SHARED_VAULT_ITEM_REMOVED, {
        item,
        removedBy: playerStats?.name
      });
    }
    return result;
  }, [removeFromSharedVault, sharedVaultItems, isDM, playerStats?.name]);

  // ============================================================================
  // DM ACTIONS (Wrapped with Events)
  // ============================================================================

  /**
   * Push shop to party (DM only).
   * [PSR-013 FIX — MAY 2026]: async + await + gate emit on success.
   * Pre-fix the systemBus emit fired before the underlying Firebase write
   * resolved — subscribers acted on writes that hadn't happened (and
   * sometimes never would, on rules denial).
   * @param {Object} shop - Shop data to push
   * @returns {Promise<boolean>} true if Firebase write succeeded
   */
  const pushShop = useCallback(async (shop) => {
    if (!pushShopToParty || !canPushContent) return false;
    const ok = await pushShopToParty(shop);
    if (ok) {
      systemBus.emit(EVENTS.DM_PUSHED_SHOP, {
        shop,
        dmName: playerStats?.name
      });
    }
    return ok;
  }, [pushShopToParty, canPushContent, playerStats?.name]);

  /**
   * Push note to party (DM only). [PSR-013 FIX]: async + gated emit.
   * @returns {Promise<boolean>}
   */
  const pushNote = useCallback(async (note) => {
    if (!pushNoteToParty || !canPushContent) return false;
    const ok = await pushNoteToParty(note);
    if (ok) {
      systemBus.emit(EVENTS.DM_PUSHED_NOTE, {
        note,
        dmName: playerStats?.name
      });
    }
    return ok;
  }, [pushNoteToParty, canPushContent, playerStats?.name]);

  /**
   * Push calendar event to party (DM only). [PSR-013 FIX]: async + gated emit.
   * @returns {Promise<boolean>}
   */
  const pushCalendarEvent = useCallback(async (event) => {
    if (!pushCalendarEventToParty || !canPushContent) return false;
    const ok = await pushCalendarEventToParty(event);
    if (ok) {
      systemBus.emit(EVENTS.DM_PUSHED_EVENT, {
        event,
        dmName: playerStats?.name
      });
    }
    return ok;
  }, [pushCalendarEventToParty, canPushContent, playerStats?.name]);

  /**
   * Push loot roll to party (DM only). [PSR-013 FIX]: async + gated emit.
   * @returns {Promise<boolean>}
   */
  const pushLootRoll = useCallback(async (loot) => {
    if (!pushLootRollToParty || !canPushContent) return false;
    const ok = await pushLootRollToParty(loot);
    if (ok) {
      systemBus.emit(EVENTS.DM_PUSHED_LOOT, {
        loot,
        dmName: playerStats?.name
      });
    }
    return ok;
  }, [pushLootRollToParty, canPushContent, playerStats?.name]);

  // ============================================================================
  // SPELL EFFECTS ON ALLIES (Wrapped with Events)
  // ============================================================================

  /**
   * Apply spell effect to ally
   * @param {string} targetId - Target character ID
   * @param {Object} effect - Effect data
   */
  // [PSR-012 FIX — MAY 2026]: async + gate emit on success.
  //   Pre-fix these wrappers fired the systemBus event before the underlying
  //   Firebase write resolved. Subscribers acted on writes that hadn't
  //   happened. Same FA-004 pattern as the vault wrappers (#121, #133).
  const castSpellOnAlly = useCallback(async (targetId, effect) => {
    if (!applyEffectToAlly) return false;
    const ok = await applyEffectToAlly(targetId, effect);
    if (ok) {
      systemBus.emit(EVENTS.SPELL_CAST_ON_ALLY, {
        targetId,
        effect,
        casterName: playerStats?.name
      });
    }
    return ok;
  }, [applyEffectToAlly, playerStats?.name]);

  /**
   * Remove spell effect from ally. [PSR-012 FIX]: async + gated emit.
   * @returns {Promise<boolean>}
   */
  const removeSpellFromAlly = useCallback(async (targetId, effectId) => {
    if (!removeEffectFromAlly) return false;
    const ok = await removeEffectFromAlly(targetId, effectId);
    if (ok) {
      systemBus.emit(EVENTS.SPELL_REMOVED_FROM_ALLY, {
        targetId,
        effectId,
        removedBy: playerStats?.name
      });
    }
    return ok;
  }, [removeEffectFromAlly, playerStats?.name]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  // [FIX APR 2026 — AUDIT C-4]: Memoize context value to prevent unnecessary consumer re-renders
  const value = useMemo(() => ({
    // Core Party State
    partyId,
    setPartyId,
    partyName,
    setPartyName,

    // Party Sync Data (from useEnhancedPartySync)
    partyMembers,
    partyActivityLog,
    isConnected,
    syncError,

    // DM Features
    isDM,
    canPushContent,
    pushedContent,

    // Shared Vault
    sharedVaultItems,
    showSharedVault,
    setShowSharedVault,

    // Spell Effects
    activeEffects,
    allPartyEffects,
    selectedAllyTarget,
    setSelectedAllyTarget,

    // Party Actions
    joinParty,
    leaveParty,
    updatePartyName,
    getPartyMember,
    getPartyMemberByName,

    // Shared Vault Actions
    addItemToSharedVault,
    takeItemFromSharedVault,
    removeItemFromSharedVault,

    // DM Actions
    pushShop,
    pushNote,
    pushCalendarEvent,
    pushLootRoll,

    // Spell Effect Actions
    castSpellOnAlly,
    removeSpellFromAlly,

    // Spec 2026-05-11 — Create/Join handlers + session-only password cache
    createPartyHandler,
    joinPartyHandler,
    getCachedCreatePassword,
  }), [
    partyId,
    setPartyId,
    partyName,
    setPartyName,
    partyMembers,
    partyActivityLog,
    isConnected,
    syncError,
    isDM,
    canPushContent,
    pushedContent,
    sharedVaultItems,
    showSharedVault,
    setShowSharedVault,
    activeEffects,
    allPartyEffects,
    selectedAllyTarget,
    setSelectedAllyTarget,
    joinParty,
    leaveParty,
    updatePartyName,
    getPartyMember,
    getPartyMemberByName,
    addItemToSharedVault,
    takeItemFromSharedVault,
    removeItemFromSharedVault,
    pushShop,
    pushNote,
    pushCalendarEvent,
    pushLootRoll,
    castSpellOnAlly,
    removeSpellFromAlly,
    createPartyHandler,
    joinPartyHandler,
    getCachedCreatePassword,
  ]);

  return (
    <PartyContext.Provider value={value}>
      {children}
    </PartyContext.Provider>
  );
};

/**
 * Hook to access PartyContext
 * @throws {Error} if used outside PartyProvider
 *
 * [FIX - NOV 3, 2025]: Enhanced error message with debugging hints
 */
export const useParty = () => {
  const context = useContext(PartyContext);
  if (!context) {
    console.error('❌ useParty() called outside PartyProvider!');
    console.error('📍 Check that your component is wrapped in <AppProviders>');
    console.error('📍 File: src/contexts/PartyContext.js');
    throw new Error(
      'useParty must be used within PartyProvider. ' +
      'Ensure your component is wrapped in <AppProviders>. ' +
      'See src/contexts/PartyContext.js:397'
    );
  }
  return context;
};

export default PartyContext;
